import type {DeferredAST, StylingDirectivesData} from '@github-ui/code-view-types'
import {useCurrentRepository} from '@github-ui/current-repository'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {deferredASTPath} from '@github-ui/paths'
import {useQuery} from '@github-ui/react-query'
import type {RefInfo} from '@github-ui/repos-types'
import {isCompactStylingDirectives} from '@github-ui/syntax-highlighted-lines/styling-directives'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import React, {useMemo} from 'react'

import {noVirtualizationThreshold} from '../components/blob/BlobContent/Code/CodeLinesNoVirtualization'

export const EmptyDeferredAST: DeferredAST = {
  stylingDirectives: null,
}

const DeferredASTContext = React.createContext(EmptyDeferredAST)

export function DeferredASTProvider({children, stylingDirectives}: React.PropsWithChildren<DeferredAST>) {
  const value = useMemo<DeferredAST>(() => ({stylingDirectives}), [stylingDirectives])
  return <DeferredASTContext value={value}>{children}</DeferredASTContext>
}

export function useDeferredAST() {
  return React.use(DeferredASTContext)
}

function isValidResponse(obj: DeferredAST): obj is DeferredAST {
  return Array.isArray(obj.stylingDirectives)
}

async function fetchDeferredAST(url: string | null): Promise<DeferredAST> {
  if (!url) return EmptyDeferredAST

  const response = await verifiedFetchJSON(url)

  if (response.ok) {
    const data = await response.json()
    if (data && isValidResponse(data)) {
      return data
    }
  }

  return EmptyDeferredAST
}

export function useDeferredASTQuery(
  refInfo: RefInfo | undefined,
  path: string | undefined,
  has404Error: boolean,
  fileLength: number,
  stylingDirectives: StylingDirectivesData | null | undefined,
  isBlob: boolean,
): DeferredAST {
  const isNonVirtualized = isFeatureEnabled('react_blob_overlay')
  const repo = useCurrentRepository()
  const url =
    refInfo && path && !has404Error
      ? deferredASTPath({
          repo,
          commitish: refInfo.name,
          path,
        })
      : null

  const hasStylingDirectives = Array.isArray(stylingDirectives)
  // Compact styled blobs already carry every line's directives, which code lines read per line.
  const hasCompactDirectives = isCompactStylingDirectives(stylingDirectives)
  const enabled =
    isBlob &&
    !hasStylingDirectives &&
    !hasCompactDirectives &&
    Boolean(url) &&
    fileLength <= noVirtualizationThreshold &&
    isNonVirtualized

  const {data} = useQuery({
    queryKey: ['deferredAST', repo.ownerLogin, repo.name, refInfo?.name, path, url],
    queryFn: () => fetchDeferredAST(url),
    enabled,
    placeholderData: EmptyDeferredAST,
  })

  if (!isBlob) return EmptyDeferredAST
  if (hasStylingDirectives) return {stylingDirectives}

  return enabled ? (data ?? EmptyDeferredAST) : EmptyDeferredAST
}
